import React from 'react';

import { DayPicker } from '../DayPicker/DayPicker';
import { TimePicker } from '../TimePicker/TimePicker';

export interface DateTimePickerProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  minDate?: Date;
  disabled?: boolean;
  classNames?: string;
  datePlaceholder?: string;
  timePlaceholder?: string;
}

const pad = (n: number): string => String(n).padStart(2, '0');

const toDateOnly = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const parseValue = (value: string): { dateOnly?: string; combined?: Date } => {
  if (!value) return {};
  const [datePart, timePart] = value.split('T');
  if (!datePart) return {};
  if (!timePart) return { dateOnly: datePart };
  const [hh, mm] = timePart.split(':').map(Number);
  const combined = new Date(`${datePart}T00:00:00`);
  if (Number.isNaN(combined.getTime())) return {};
  combined.setHours(hh || 0, mm || 0, 0, 0);
  return { dateOnly: datePart, combined };
};

export const DateTimePicker = ({
  id,
  value,
  onChange,
  minDate,
  disabled,
  classNames,
  datePlaceholder,
  timePlaceholder,
}: DateTimePickerProps): JSX.Element => {
  const { dateOnly, combined } = parseValue(value);
  const selectedDate = dateOnly ? new Date(`${dateOnly}T00:00:00`) : undefined;

  const emit = (nextDateOnly?: string, nextTime?: Date): void => {
    if (!nextDateOnly) {
      onChange('');
      return;
    }
    if (!nextTime) {
      onChange(nextDateOnly);
      return;
    }
    onChange(`${nextDateOnly}T${pad(nextTime.getHours())}:${pad(nextTime.getMinutes())}`);
  };

  return (
    <div className={`flex gap-2${classNames ? ` ${classNames}` : ''}`}>
      <DayPicker
        id={id}
        selected={selectedDate}
        minDate={minDate}
        disabled={disabled}
        placeholderText={datePlaceholder}
        onChange={(date: Date | null): void => emit(date ? toDateOnly(date) : undefined, combined)}
      />
      <TimePicker
        selected={combined}
        disabled={disabled}
        placeholderText={timePlaceholder}
        onChange={(time: Date | null): void => emit(dateOnly, time ?? undefined)}
      />
    </div>
  );
};
